import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Mail, Phone, RefreshCw, Sparkles, User } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@command-center/ui-system';
import {
  CompactRecordList,
  CompactRecordListItem,
  CollectionViewToggle,
  DetailSection,
  Pagination,
  RecordCard,
  RecordCardGrid,
  RecordDetailHeading,
  RecordDetailShell,
  RecordTable,
  RecordTableCell,
  RecordTablePrimaryCell,
  RecordTableRow,
  RecordTabs,
  ListToolbar,
  SearchField,
  WorkspaceSplitView,
  useCollectionView,
  useIsSplitView,
} from '@command-center/ui-system';
import { ActivityTimeline, DetailTabBody, SimpleNotesPanel, RelatedRecordList } from '../components/record-detail-panels';
import { LinkedIntakeRecords } from '../components/linked-intake-records';
import { EmailLink } from './SendEmailDialog';
import { leadloopApi, type CascadeContactListItem } from '../lib/leadloop-api';
import {
  useLoadedRecordDetails,
  type RightSidebarRecordDetails,
} from '@command-center/oneup-portal-ui';
import { usePagedView } from '../lib/use-paged-view';
import {
  Cell,
  CountCell,
  DateCell,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  formatDateTime,
} from './parts';

export function ContactsView() {
  const [q, setQ] = useState('');
  const [view, setView] = useCollectionView('cascade:records:contacts:view');
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { page, setPage, pageSize } = usePagedView([q]);
  const contacts = useQuery({
    queryKey: ['contacts', q, page, pageSize],
    queryFn: () => leadloopApi.contacts(q || undefined, { page, pageSize }),
    placeholderData: (previous) => previous,
  });
  const items = useMemo(() => contacts.data?.items ?? [], [contacts.data?.items]);
  const selectedIndex = id ? items.findIndex((item) => item.id === id) : -1;
  const detailPanel = useMemo(
    () => id ? (
      <ContactDetailPanel
        id={id}
        embedded
        fallback={items[selectedIndex]}
        onClose={() => navigate('/contacts')}
        onPrevious={selectedIndex > 0 ? () => navigate(`/contacts/${items[selectedIndex - 1]!.id}`) : undefined}
        onNext={selectedIndex >= 0 && selectedIndex < items.length - 1 ? () => navigate(`/contacts/${items[selectedIndex + 1]!.id}`) : undefined}
      />
    ) : undefined,
    [id, items, navigate, selectedIndex],
  );
  const loadedRecord = useMemo<RightSidebarRecordDetails | undefined>(
    () => id && detailPanel ? {
      key: `contact:${id}`,
      recordId: id,
      recordTypeKey: 'contact',
      title: items[selectedIndex]?.fullName ?? id,
      content: detailPanel,
      sourcePath: '/contacts',
      onClear: () => navigate('/contacts'),
      canGoPrevious: selectedIndex > 0,
      canGoNext: selectedIndex >= 0 && selectedIndex < items.length - 1,
      onPrevious: () => navigate(`/contacts/${items[selectedIndex - 1]!.id}`),
      onNext: () => navigate(`/contacts/${items[selectedIndex + 1]!.id}`),
    } : undefined,
    [detailPanel, id, items, navigate, selectedIndex],
  );
  useLoadedRecordDetails(loadedRecord, Boolean(id));

  const pagination = (
    <Pagination
      page={page}
      pageSize={pageSize}
      total={contacts.data?.total}
      count={items.length}
      onPageChange={setPage}
      busy={contacts.isFetching}
      label="contacts"
    />
  );

  // Below the split the detail replaces the list; searching a list nobody can
  // see is just furniture at the top of a phone screen.
  const listVisible = useIsSplitView() || !id;

  return (
    <div className="flex flex-col gap-4">
      {listVisible && (
        <ListToolbar
          search={<SearchField value={q} onChange={setQ} placeholder="Search contacts…" label="Search contacts" />}
          count={`${contacts.data?.total ?? items.length} contacts`}
          viewToggle={!id && <CollectionViewToggle view={view} onChange={setView} />}
        />
      )}
      {contacts.isLoading ? (
        <LoadingState />
      ) : contacts.isError ? (
        <ErrorState error={contacts.error} />
      ) : items.length === 0 ? (
        <EmptyState label="No contacts match." />
      ) : (
        <WorkspaceSplitView>
          {id ? (
            <CompactRecordList title={`${contacts.data?.total ?? items.length} contacts`}>
              {items.map((contact) => (
                <CompactRecordListItem
                  key={contact.id}
                  selected={contact.id === id}
                  title={contact.fullName}
                  subtitle={contact.companyName ?? contact.email ?? 'No company'}
                  meta={[contact.email, contact.phone].filter(Boolean).join(' · ')}
                  onSelect={() => navigate(`/contacts/${contact.id}`)}
                />
              ))}
            </CompactRecordList>
          ) : view === 'cards' ? (
            <div className="flex flex-col gap-4">
              <RecordCardGrid>
                {items.map((contact) => (
                  <RecordCard
                    key={contact.id}
                    recordType="contact"
                    icon={User}
                    title={contact.fullName}
                    subtitle={
                      contact.organizationName ? (
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="h-3 w-3" aria-hidden />
                          {contact.organizationName}
                        </span>
                      ) : (
                        contact.roleTitle ?? 'No organization'
                      )
                    }
                    details={[
                      {
                        label: 'Phone',
                        value: contact.phone && (
                          <a className="text-primary hover:underline" href={`tel:${contact.phone}`}>
                            {contact.phone}
                          </a>
                        ),
                      },
                      {
                        label: 'Email',
                        value: <EmailLink email={contact.email} name={contact.fullName} />,
                      },
                      { label: 'Role', value: contact.roleTitle },
                      {
                        label: 'Last activity',
                        value: contact.lastActivityAt ? formatDateTime(contact.lastActivityAt) : undefined,
                      },
                    ]}
                    meta={
                      <>
                        <span>
                          {contact.totalLeadCount === 0
                            ? 'No leads'
                            : `${contact.totalLeadCount} lead${contact.totalLeadCount === 1 ? '' : 's'}`}
                          {contact.openLeadCount > 0 && ` · ${contact.openLeadCount} open`}
                        </span>
                        <span>Added {formatDateTime(contact.createdAt)}</span>
                      </>
                    }
                    onOpen={() => navigate(`/contacts/${contact.id}`)}
                    openLabel="Open contact"
                  />
                ))}
              </RecordCardGrid>
              {pagination}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <RecordTable
                columns={[
                  { key: 'name', header: 'Name' },
                  { key: 'org', header: 'Organization' },
                  { key: 'phone', header: 'Phone', hideOnMobile: true },
                  { key: 'email', header: 'Email', hideOnMobile: true },
                  { key: 'leads', header: 'Leads', align: 'right' },
                  { key: 'activity', header: 'Last activity', hideOnMobile: true },
                  { key: 'added', header: 'Added', hideOnMobile: true },
                ]}
              >
                {items.map((contact) => (
                  <RecordTableRow key={contact.id} onOpen={() => navigate(`/contacts/${contact.id}`)}>
                    <RecordTablePrimaryCell title={contact.fullName} subtitle={contact.roleTitle} width={200} />
                    <RecordTableCell><Cell value={contact.organizationName} width="20ch" /></RecordTableCell>
                    <RecordTableCell hideOnMobile>
                      {contact.phone && (
                        <a
                          className="text-primary hover:underline"
                          href={`tel:${contact.phone}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {contact.phone}
                        </a>
                      )}
                    </RecordTableCell>
                    <RecordTableCell hideOnMobile>
                      <Cell value={<EmailLink email={contact.email} name={contact.fullName} />} width="24ch" />
                    </RecordTableCell>
                    <RecordTableCell align="right">
                      <CountCell value={contact.totalLeadCount} />
                      {contact.openLeadCount > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">({contact.openLeadCount} open)</span>
                      )}
                    </RecordTableCell>
                    <RecordTableCell hideOnMobile><DateCell value={contact.lastActivityAt} /></RecordTableCell>
                    <RecordTableCell hideOnMobile><DateCell value={contact.createdAt} /></RecordTableCell>
                  </RecordTableRow>
                ))}
              </RecordTable>
              {pagination}
            </div>
          )}
        </WorkspaceSplitView>
      )}
    </div>
  );
}

function ContactDetailPanel({
  id,
  fallback,
  onClose,
  onPrevious,
  onNext,
  embedded = false,
}: {
  id: string;
  fallback?: CascadeContactListItem;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  embedded?: boolean;
}) {
  const [tab, setTab] = useState('overview');
  const [refreshMessage, setRefreshMessage] = useState<string>();
  const queryClient = useQueryClient();
  useEffect(() => {
    setTab('overview');
    setRefreshMessage(undefined);
  }, [id]);
  const detail = useQuery({
    queryKey: ['contact', id],
    queryFn: () => leadloopApi.contactDetail(id),
  });
  const activity = useQuery({
    queryKey: ['activity', id],
    queryFn: () => leadloopApi.activity({ entityId: id, limit: 100 }),
  });
  const contact = detail.data?.contact ?? fallback;
  const intake = detail.data?.intake ?? [];
  const refreshOverview = useMutation({
    mutationFn: () => leadloopApi.refreshContactOverview(id, crypto.randomUUID()),
    onSuccess: async (result) => {
      setRefreshMessage(result.changed ? 'AI Contact Overview refreshed.' : 'Overview is already up to date.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contact', id] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
      ]);
    },
    onError: () => setRefreshMessage(undefined),
  });

  return (
    <RecordDetailShell
      embedded={embedded}
      recordType="contact"
      onClose={onClose}
      onPrevious={onPrevious}
      onNext={onNext}
    >
      {detail.isLoading && !contact ? (
        <LoadingState />
      ) : detail.isError ? (
        <ErrorState error={detail.error} />
      ) : contact ? (
        <>
          <RecordDetailHeading
            icon={User}
            title={contact.fullName}
            badges={
              <span className="text-xs text-muted-foreground">
                {contact.roleTitle ?? contact.type}
              </span>
            }
          />
          <RecordTabs
            active={tab}
            onChange={setTab}
            tabs={[
              { id: 'overview', label: 'Overview' },
              { id: 'linked', label: 'Linked records', count: detail.data?.leads.length ?? 0 },
              { id: 'history', label: 'Calls & Forms', count: intake.length },
              { id: 'activity', label: 'Activity' },
            ]}
          />
          <DetailTabBody>
          {tab === 'overview' && <>
          <DetailSection title="Contact details">
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field
                label="Email"
                value={
                  <EmailLink
                    email={contact.email}
                    name={contact.fullName}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" aria-hidden />
                    {contact.email}
                  </EmailLink>
                }
              />
              <Field
                label="Phone"
                value={
                  contact.phone && (
                    <a
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      href={`tel:${contact.phone}`}
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {contact.phone}
                    </a>
                  )
                }
              />
              <Field label="Company" value={contact.companyName} />
              <Field label="Type" value={contact.type} />
              <Field label="Created" value={formatDateTime(contact.createdAt)} />
              <Field label="Updated" value={formatDateTime(contact.updatedAt)} />
            </dl>
          </DetailSection>
          <DetailSection title="Organization">
            {detail.data?.organization ? (
              <Link
                to={`/organizations/${detail.data.organization.id}`}
                className="text-sm font-medium text-primary hover:underline"
              >
                {detail.data.organization.name}
              </Link>
            ) : (
              <p className="text-sm text-muted-foreground">No linked organization.</p>
            )}
          </DetailSection>
          <DetailSection title="AI Contact Overview">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5" />
                  AI-generated · Read-only
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  disabled={
                    refreshOverview.isPending
                    || detail.data?.overviewStatus?.status === 'processing'
                    || detail.data?.overviewStatus?.enabled === false
                    || !contact.callerContactId
                  }
                  onClick={() => refreshOverview.mutate()}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${refreshOverview.isPending ? 'animate-spin' : ''}`} />
                  {refreshOverview.isPending ? 'Refreshing…' : 'Refresh AI Overview'}
                </Button>
              </div>
              {contact.aiOverview ? (
                <p className="whitespace-pre-wrap text-sm leading-6 text-foreground">{contact.aiOverview}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No overview yet. It will update after linked activity, or you can refresh it now.
                </p>
              )}
              {detail.data?.overviewStatus?.enabled === false && (
                <p className="text-xs text-muted-foreground">
                  Automatic and manual updates are paused by the Contact Intelligence Switch.
                </p>
              )}
              {!contact.callerContactId && (
                <p className="text-xs text-muted-foreground">
                  Link this record to a Cascade Contact before refreshing the overview.
                </p>
              )}
              {detail.data?.overviewStatus?.lastRefreshedAt && (
                <p className="text-xs text-muted-foreground">
                  Last refreshed {formatDateTime(detail.data.overviewStatus.lastRefreshedAt)}
                </p>
              )}
              {refreshMessage && <p className="text-xs text-muted-foreground">{refreshMessage}</p>}
              {refreshOverview.isError && (
                <p className="text-xs text-destructive">
                  {refreshOverview.error instanceof Error
                    ? refreshOverview.error.message
                    : 'The overview could not be refreshed.'}
                </p>
              )}
            </div>
          </DetailSection>
          <DetailSection title="At a glance">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Metric label="Leads" value={detail.data?.leads.length ?? 0} />
              <Metric label="Forms" value={intake.filter((item) => item.sourceType === 'form_submission').length} />
              <Metric label="Calls" value={intake.filter((item) => item.sourceType === 'call').length} />
            </div>
          </DetailSection>
          {/* Notes close the record rather than hiding behind a tab, matching
              calls and form entries: adding context is what you do after
              reading, so it belongs at the end of the reading. */}
          <SimpleNotesPanel
            entityType="contact"
            entityId={contact.id}
            entityLabel={contact.fullName}
            activity={activity.data?.items ?? []}
          />
          </>}
          {tab === 'linked' && <>
            <DetailSection title="Lead records"><RelatedRecordList empty="No leads are linked or matched to this contact." items={(detail.data?.leads ?? []).map((lead) => ({ id: lead.id, to: `/leads/${lead.id}`, title: lead.title, subtitle: lead.statusLabel, meta: formatDateTime(lead.createdAt) }))} /></DetailSection>
          </>}
          {tab === 'history' && <LinkedIntakeRecords items={intake} />}
          {tab === 'activity' &&<DetailSection title="Activity and history"><ActivityTimeline items={activity.data?.items ?? []} /></DetailSection>}
          </DetailTabBody>
        </>
      ) : null}
    </RecordDetailShell>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return <div className="rounded-md border border-border bg-muted/40 px-2 py-3"><p className="text-lg font-semibold text-foreground">{value}</p><p className="text-[11px] text-muted-foreground">{label}</p></div>;
}
